import type { ObjectId } from 'mongodb';

import type { HarnessRole } from './AccountctlRun';
import type { Address, PlacedInServiceTrigger, VerificationDaysTrigger } from './AccountTypes';
import type { InstallAttestmentType, PermitAttestmentSelection } from './Attestment';
import type { DirectPayMaterialVendorAliasOptions } from './DirectPayTypes';
import { DisclosureType } from './Disclosures';
import type { InverterType } from './DomesticContentTypes';
import type { PickEnumValues, StateCode } from './Helpers';
import { LineItemCategories, LineItemTypes } from './InboundPayments';
import { ProgramType } from './ProgramTypes';
import type { HoldMotionQueueType } from './Queues';
import type { SalesRepStatus } from './SalesRepTypes';
import { AccountStipulationReason } from './Stipulations';
import type { WarrantyProvider } from './Warranties';

import type { FinancingDecision, Locale } from './index';

export enum AppSettingsTypes {
  accountctlTestUsers = 'accountctlTestUsers',
  autoCompleteRequirements = 'autoCompleteRequirements',
  approvedVendorList = 'approvedVendorList',
  cedSplitPayPrograms = 'cedSplitPayPrograms',
  /** @deprecated clawback settings have moved to an organization setting. */
  clawbacks = 'clawbacks',
  concord = 'concord',
  customQuoteSettings = 'customQuoteSettings',
  designTools = 'designTools',
  disclosures = 'disclosures',
  disallowedEmails = 'disallowedEmails',
  eightyNinetyOptInSettings = 'eightyNinetyOptInSettings',
  imports = 'imports',
  exports = 'exports',
  inboundPayment = 'inboundPayment',
  inboundPaymentsCustomerTaxSettings = 'inboundPaymentsCustomerTaxSettings',
  illinoisShineDisclosureVendorId = 'illinoisShineDisclosureVendorId',
  monthlyCapByFico = 'monthlyCapByFico',
  netsuite = 'netSuite',
  notifications = 'notifications',
  orgContractRequirements = 'orgContractRequirements',
  pricing = 'pricing',
  priceSheetMappingLock = 'priceSheetMappingLock',
  productCatalogClassificationMapping = 'productCatalogClassificationMapping',
  quoteValidation = 'quoteValidation',
  reductionRateHoldbacks = 'reductionRateHoldbacks',
  requestRetry = 'requestRetry',
  stateLicenseRequirements = 'stateLicenseRequirements',
  stateSalesRepEmailRequirements = 'stateSalesRepEmailRequirements',
  stateSolarIncentives = 'stateSolarIncentives',
  stipulations = 'stipulations',
  testSSNs = 'testSSNs',
  testSSNsWithFico = 'testSSNsWithFico',
  /** @deprecated use `OrganizationSettingsV2Types.underwritingSettings` instead */
  underwriting = 'underwriting',
  utilityNetMeteringRates = 'utilityNetMeteringRates',
  directPayMaterialVendors = 'directPayMaterialVendors',
  warrantyProviders = 'warrantyProviders',
  domesticContent = 'domesticContent',
  llmConfig = 'llmConfig',
  salesforce = 'salesforce',
  contractVaulting = 'contractVaulting',
  routeAllowlist = 'routeAllowlist',
  ptoGreenlight = 'ptoGreenlight',
  verificationDays = 'verificationDays',
  operationalAtRequirements = 'operationalAtRequirements',
  placedInServiceRequirements = 'placedInServiceRequirements',
  buyout = 'buyout',
  fileExtensionWhitelist = 'fileExtensionWhitelist',
  feocAccountExceptions = 'feocAccountExceptions',
  frozenCreditEmailProgramTypes = 'frozenCreditEmailProgramTypes',
  unbatchedTransactionsOrgExclusions = 'unbatchedTransactionsOrgExclusions',
  blockBillingSettings = 'blockBillingSettings',
  holdQueueSettings = 'holdQueueSettings',
  holdMotionProgramSettings = 'holdMotionProgramSettings',
  installAttestmentCopy = 'installAttestmentCopy',
  welcomeCall = 'welcomeCall',
  qcPriorityOperatingHours = 'qcPriorityOperatingHours',
  annexIISerialReference = 'annexIISerialReference',
  cancellationCronJobSettings = 'cancellationCronJobSettings',
  systemLossThresholds = 'systemLossThresholds',
}

export interface IAppSettingsDocument {
  id: ObjectId;
  type: AppSettingsTypes;
  value: Record<string, any>;
  programType?: ProgramType;
}

export interface ICEDSplitPayProgramNamesSettingsDocument extends IAppSettingsDocument {
  value: {
    programs: {
      programId: string;
      programName: string;
    };
    parallelPayShipConfirmPaymentPercentage: number;
    cedOrgAlias: string;
  };
}

export interface IPtoGreenlightSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.ptoGreenlight;
  value: {
    autoGrantInitialProcessDate: Date;
  };
}

export interface ICustomQuoteSettingsDocument extends IAppSettingsDocument {
  value: {
    approvedOrganizations: {
      organizationId: string;
      maxEscalationRate: number;
    }[];
  };
}

export interface IDesignTools extends IAppSettingsDocument {
  value: Record<string, string>;
}

export interface I8090OptInSettingsDocument extends IAppSettingsDocument {
  value: {
    percentOfProjects: number;
    states: Array<string>;
    organizations: Array<string>;
  };
}

export interface IDirectPayMaterialsVendorSettings {
  directPayMaterialVendorAlias: string;
  directPayMaterialVendorName: string;
  incomingWebhookAPIKey: string;
  outgoingAPICallBaseUrl: string;
  outgoingAPICallAPIKey?: string;
  outgoingAPICallClientId?: string;
  downloadInvoiceDocuments?: boolean;
  subCompanies?: Array<{ name: string; id: string }>;
}

export interface ISunrgyDirectPayConfig extends IDirectPayMaterialsVendorSettings {
  createProjectPublicKey: string;
  getProjectPublicKey: string;
  updateProjectPublicKey: string;
  getInvoicesPublicKey: string;
  getInvoiceDocumentPublicKey: string;
}

export interface IDirectPayMaterialVendorsSettingsDocument extends IAppSettingsDocument {
  value: {
    materialVendors: IDirectPayMaterialsVendorSettings[];
  };
}

export interface IQuoteValidationSettingsDocument extends IAppSettingsDocument {
  value: {
    preConAdderLimit: number;
    maxProductionDecrease: number;
    maxProductionIncrease: number;
    maxOveragePercentage: number;
  };
}

export interface IPricingSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: {
    productionFactorRoundingThreshold: number;
    monthlyPaymentPanelDegradationRate: number;
    performanceGuaranteePanelDegradationRate: number;
    annualProductionGuaranteePercentage: number;
    agreementLength: number;
  };
}

export interface IBuyoutSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: {
    endOfTermBuyoutPercentage: number;
    npvBuyoutPercent: number;
    npvBuyoutFormula: string;
  };
}

export interface IInboundPaymentSettingsDocument extends IAppSettingsDocument {
  value: {
    invoiceDueDay: number;
    billingDay: number;
    gracePeriodDurationInDays: number;
  };
}

export interface IDisclosuresSettingsDocument extends IAppSettingsDocument {
  value: {
    activeDisclosureVersions: Array<{ type: DisclosureType; version: number }>;
  };
}

export interface IDisallowedEmailsSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.disallowedEmails;
  value: {
    creditCheckBlockedEmails: string[];
  };
}

export interface StateRequirement {
  state: string;
  required: boolean;
  effectiveDate: Date;
}
export interface IStateLicenseRequirementsDocument extends IAppSettingsDocument {
  value: {
    requiresLicense: Array<StateRequirement>;
  };
}

export interface IStateSalesRepEmailRequirementDocument extends IAppSettingsDocument {
  value: {
    requiresSalesRepEmail: Array<StateRequirement>;
  };
}

export interface IInboundPaymentsCustomerTaxSettingsDocument extends IAppSettingsDocument {
  value: {
    state: {
      [key: StateCode]: {
        [key in LineItemCategories]: {
          [k in LineItemTypes]: InboundPaymentsCustomerTaxSetting;
        };
      };
    };
  };
}

export interface IIllinoisShineDisclosureVendorIdSettingsDocument extends IAppSettingsDocument {
  value: {
    vendorId: number;
  };
}

export interface INetSuiteSettingsDocument extends IAppSettingsDocument {
  value: {
    invoices: {
      account: string;
      subsidiary: string;
      location: string;
      lineItems: NetSuiteInvoiceLineItemSetting[];
    };
  };
}

export interface IRateReductionHoldbacksSettingDocuments extends IAppSettingsDocument {
  value: {
    priceSettingHoldbacks: ObjectId[];
  };
}

export interface NetSuiteInvoiceLineItemSetting {
  type: 'item' | 'tax';
  description: string;
  stripeId?: string;
  netSuiteId: string;
}

export interface InboundPaymentsCustomerTaxSetting {
  stripeTaxCode: StripeSolarTaxCode;
}

export enum StripeSolarTaxCode {
  // These are typically taxed
  TangibleGoods = 'txcd_99999999',

  // These are typically exempt from taxation
  Services = 'txcd_20030000',
}

export interface StateFieldRequirements {
  state: string;
  organizationRequiredFields: Array<string>;
  licenseRequiredFields: Array<string>;
}

export interface OrgContractRequirementsSettings extends IAppSettingsDocument {
  value: {
    stateFieldRequirements: Array<StateFieldRequirements>;
  };
}

export interface ContractVaultSettings extends IAppSettingsDocument {
  value: {
    /** The field ID of a DocusSign template custom field to control exporting to eOriginal eVault.
     * Can be obtained from the DocuSign portal. */
    eOriginalTemplateCustomFieldId: string;
  };
}

export interface RouteAllowlistEntry {
  organizationIds: string[];
  /** Auth0 user ids (`sub`). */
  userIds: string[];
}

/**
 * Per-route caller allowlist. Keys are `"<METHOD> <route pattern>"` exactly as Fastify
 * registers them, e.g. `"GET /api/v2/accounts/:accountId/contracts/current/signing-url"`.
 * A route gated by `requireRouteAllowlist` with no entry here denies everyone.
 */
export interface IRouteAllowlistSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.routeAllowlist;
  value: {
    routes: Record<string, RouteAllowlistEntry>;
  };
}

/** @deprecated Use `OrganizationSettingsV2<OrganizationUnderwritingSettingsV2>` instead. */
export interface UnderwritingSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: {
    minFicoScore: number;
    ficoScoreErrorThreshold: number;
    creditExpiryTerm: number;
    yearsSinceLastBankruptcy: number;
    minFicoToRerun: number;
    maxAppsPerDay: number;
    autoQualify?: boolean;
  };
}

export enum VendorEquipmentTypes {
  inverter = 'inverter',
  panel = 'panel',
  mounting = 'mounting',
  storage = 'storage',
  heatPump = 'heatPump',
  airHandler = 'airHandler',
  heatStrip = 'heatStrip',
  thermostat = 'thermostat',
  furnace = 'furnace',
  waterHeater = 'waterHeater',
}

export type AvlImportSheetMapping = PickEnumValues<
  typeof VendorEquipmentTypes,
  'inverter' | 'panel' | 'mounting' | 'storage'
>;

export enum ApprovedVendorAllocation {
  full = 'full',
  limited = 'limited',
  exception = 'exception',
  preApproval = 'preApproval',
  safeHarbor = 'safeHarbor',
  deprecated = 'deprecated',
}

export enum AvlFilterOption {
  dcEligibleOnly = 'dcEligibleOnly',
  itcCalculator = 'itcCalculator',
}

/** These map to cell values, so the values are purposely set as such. */
export enum CalculatorType {
  /** Show in all calculators */
  AllCalculators = 'All Calculators',
  itcCalculator = 'ITC Calculator',
}

export type FeocDataItem = {
  /** Inverters and batteries will have a real vintage, for other hardware the vintage is "none" */
  vintage: string;
  subSku?: string;
  mlpeDcPercentage?: number;
  stringDcPercentage?: number;
  mlpeFeocPercentage?: number;
  stringFeocPercentage?: number;
  bessFeocPercentage?: number;
  bessDcPercentage?: number;
};

export type ApprovedVendorSettingValue = {
  id: string;
  name: string;
  modelNumber: string;
  manufacturer: string;
  allocation: ApprovedVendorAllocation;
  calculatorType?: CalculatorType;
  sku?: string;
  description?: string;
  category?: string;
  domesticContentSupported?: boolean;
  inverterType?: InverterType;
  bessDomesticContentPercentage?: number;
  bessFeocPercentage?: number;
  domesticContentPercentageMlpe?: number;
  domesticContentPercentageString?: number;
  feocPercentageMlpe?: number;
  feocPercentageString?: number;
  needsApproval: boolean;
  maxDCkW?: number;
  maxACkW?: number;
  gridPairedACkW?: number | null;
  gridPairedPeakACkW?: number | null;
  usableCapacityKWh?: number | null;
  aliases?: string[];
  /** Old per-programType ids from the legacy appSettings AVL, retained for reference compatibility. */
  legacyIds?: string[];
  omnidianAlias?: string;
  maxPrice?: number;
  requiresExtendedWarranty?: boolean;
  /** Any specific Safe Harbor vintage data */
  feocData?: FeocDataItem[];
  extendedWarrantyCost?: number;
  size?: {
    value: number;
    unit: 'kW' | 'kWh' | 'watts';
  };
  arbitrage?: boolean;
  backup?: boolean;
};

export type ApprovedVendorSettings = {
  [key in VendorEquipmentTypes]: Array<ApprovedVendorSettingValue>;
};
export interface ApprovedVendorSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: ApprovedVendorSettings;
}

export type StipulationSettingValue = {
  /** Send stip emails */
  sendCommunication?: boolean;

  /** Time in seconds */
  webhookDelay?: number;

  /** Solar rules. Predates per-program rules, so it stays here rather than moving into `rulesByProgram`. */
  rules?: WelcomeCallStipRulesValue;

  /** Rules for programs other than solar. */
  rulesByProgram?: Partial<Record<ProgramType, WelcomeCallStipRulesValue>>;
};

export type StipulationSettings = {
  [key in AccountStipulationReason]?: StipulationSettingValue;
};

export interface StipulationsSettingsDocument extends IAppSettingsDocument {
  value: StipulationSettings;
}

export interface PriceSheetMappingLockDocument extends IAppSettingsDocument {
  value: {
    days?: number;
  };
}

export enum SolarIncentiveUnit {
  dollars = 'dollars',
  dollarPerKWh = 'dollarPerKWh',
  dollarPerWattDC = 'dollarPerWattDC',
}

export interface StateSolarIncentivesDocument extends IAppSettingsDocument {
  value: {
    state: string;
    incentiveName: string;
    amount: number;
    unit: SolarIncentiveUnit;
    docusignFieldName: string;
    lseIds: number[];
  }[];
}

export interface UtilityNetMeteringSettingsDocument extends IAppSettingsDocument {
  value: {
    state: string;
    lseId: number;
    utilityName: string;
    fieldValue: string;
    docusignFieldName: string;
  }[];
}

/** @deprecated clawback settings have moved to an organization setting. */
export interface ClawbackSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: {
    /** Cancelled at least these many days ago */
    cancellationClawbackDays: number;
    /** NTP payment was made, but install has not occurred in these days */
    ntpClawbackDays: number;
    /** Install payment was made, but activation has not occurred in these days */
    installClawbackDays: number;
    /** We have paid a materials vendor (CED), but EPC has not submitted install milestone within these days */
    partnerInstallNotCompleteDays: number;
  };
}

export interface NotificationSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  value: {
    /** Whether to send _any_ notifications via SolarFlare for this program type */
    enabled: boolean;
    templateMap: {
      /** [key in MailTemplates]: Other mail template id, if different */
      [key: string]: string;
    };
  };
}

export interface FicoRange {
  minScore: number;
  maxScore: number;
  zeroEscalationRate: number;
  greaterThanZeroEscalationRate: number;
}

/** @deprecated Use `OrganizationMonthlyCapByFicoSettings` instead. */
export interface MonthlyCapByFicoDocument {
  type: AppSettingsTypes.monthlyCapByFico;
  version: number;
  value: {
    ranges: FicoRange[];
  };
  programType: ProgramType;
}

export interface TestSsnConfig {
  status: FinancingDecision;
  ficoScore: number;
  hasCreditFraudAlert?: boolean;
  creditFraudAlertText?: string;
}

export interface TestSSNsWithFicoDocument {
  type: AppSettingsTypes.testSSNsWithFico;
  value: Record<string, TestSsnConfig>;
}

export interface RetryPolicyConfig {
  maxRetries: number;
  backoffMs: number;
  initialDelayMs?: number;
  exponent?: number;
}

export interface VendorRetryConfig extends RetryPolicyConfig {
  getProjectInvoices?: RetryPolicyConfig;
  getProjectInvoiceDocuments?: RetryPolicyConfig;
}

export interface RetrySettingsDocument extends IAppSettingsDocument {
  value: {
    onPointWarranty: {
      maxRetries: number;
      backoffMs: number;
      initialDelayMs: number;
      exponent?: number;
    };
    directPay: VendorRetryConfig;
  } & Partial<Record<DirectPayMaterialVendorAliasOptions, VendorRetryConfig>>;
}

export interface ExportSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.exports;
  value: {
    accountsLimit: number;
  };
}

export interface ImportSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.imports;
  value: {
    avlImport: {
      spreadsheets: {
        spreadsheetId: string;
        name: string;
        tabNames: Record<AvlImportSheetMapping, string>;
      }[];
    };
  };
}

export interface WarrantyProvidersDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.warrantyProviders;
  value: {
    providers: Array<{
      name: WarrantyProvider;
      default: boolean;
    }>;
  };
}

export interface DomesticContentSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.domesticContent;
  value: {
    credits: {
      /** @deprecated */
      rackingRailPercentage: Record<InverterType, number>;
      rackingFastenerPercentage: Record<InverterType, number>;
      rackingProductionCreditPercentage: Record<InverterType, number>;
    };
  };
}

export interface IOperationAtRequirementObj {
  isRequired: boolean;
  isRequiredInPR?: boolean;
  triggerEvent?: VerificationDaysTrigger;
}

export interface IOperationalAtRequirementsSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  type: AppSettingsTypes.operationalAtRequirements;
  value: {
    maxVerificationDays: number;
    [VerificationDaysTrigger.installApproved]: IOperationAtRequirementObj;
    [VerificationDaysTrigger.trancheClosed]: IOperationAtRequirementObj;
    [VerificationDaysTrigger.ptoConfirmation]: IOperationAtRequirementObj;
    [VerificationDaysTrigger.trueUpSize]: IOperationAtRequirementObj;
    [VerificationDaysTrigger.electricianSignOff]: IOperationAtRequirementObj;
  };
}

export interface IPlacedInServiceRequirementsSettingsDocument extends IAppSettingsDocument {
  programType: ProgramType;
  type: AppSettingsTypes.placedInServiceRequirements;
  value: {
    [PlacedInServiceTrigger.operationAt]: {
      isRequired: boolean;
    };
    [PlacedInServiceTrigger.measurableGeneration]: {
      isRequired: boolean;
    };
    [PlacedInServiceTrigger.trancheClosed]: {
      isRequired: boolean;
    };
  };
}

export interface FeocAccountExceptionSettings extends IAppSettingsDocument {
  type: AppSettingsTypes.feocAccountExceptions;
  programType: ProgramType;
  value: {
    exceptions: { address?: Address; externalReference?: string }[];
  };
}

export interface BillingBlockReason {
  id: string;
  label: string;
  description?: string;
  show: boolean;
}

export interface BlockBillingSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.blockBillingSettings;
  value: {
    reasons: BillingBlockReason[];
  };
}

export interface FrozenCreditEmailProgramTypesDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.frozenCreditEmailProgramTypes;
  value: { programTypes: ProgramType[] };
}
export interface IHoldQueueSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.holdQueueSettings;
  value: {
    queueType: HoldMotionQueueType;
    enabled: boolean;
  }[];
}

export interface IHoldMotionProgramSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.holdMotionProgramSettings;
  value: { programType: ProgramType; enabled: boolean }[];
}

/**
 * Shared list of test users the accountctl harness can authenticate as when it
 * runs through the in-app UI. Replaces (and supersedes for the UI) the on-disk
 * `localOnly/accountctl-users.json` file the CLI uses.
 *
 * Passwords are stored in plaintext. This is intentional and gated by:
 *   1. The non-prod env guard on `/api/internal/accountctl/*` — these routes
 *      never mount in prod, so the doc can never be read or written there.
 *   2. The `requireInternalUser` preHandler — only Palmetto Finance internal
 *      users can hit the editor or the read endpoint.
 *
 * Operators editing this doc should treat it as test-account-only and must NOT
 * paste real customer or production credentials in. The UI surfaces a banner
 * to that effect.
 */
export interface IAccountctlTestUser {
  /** Stable human-readable label so operators can tell entries apart. */
  label: string;
  /** Which harness role this user fills (drives credential resolution). */
  role: HarnessRole;
  email: string;
  /** Plaintext, non-prod only. See doc comment on the document interface. */
  password: string;
  /** Organization alias for the user, used for drift checks at auth time. */
  organizationAlias?: string;
}

export interface IAccountctlTestUsersSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.accountctlTestUsers;
  value: {
    users: IAccountctlTestUser[];
  };
}

export enum WelcomeCallProvider {
  recheck = 'recheck',
}

/** Per-signal risk-decision config. A signal is evaluated only when its config field is present. */
export interface WelcomeCallRiskDecision {
  /** Apply when primary applicant age is strictly greater than this. */
  ageThreshold?: number;
  /** Apply when the contracted quote's monthly payment is strictly greater than this. */
  monthlyPaymentThreshold?: number;
  /** Apply when the account's sales rep has one of these statuses. */
  flaggedSalesRepStatuses?: SalesRepStatus[];
  /** Apply when the account's preferred language is Spanish. */
  matchSpanishLanguage?: boolean;
}

/** Provider-specific Welcome Call configuration. Every {@link WelcomeCallProvider} must be represented. */
export type WelcomeCallProviderSettings = Record<
  WelcomeCallProvider,
  {
    /** Verification-call template id. */
    templateId: string;
  }
>;

/**
 * Per-`programType` Welcome Call configuration. `provider` and `riskDecision` are
 * provider-independent; provider-specific config (e.g. the Recheck template id) lives
 * under `providerSettings`. Adding signals beyond `programType` should extend `value`
 * rather than introducing a new settings type.
 */
export interface WelcomeCallSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.welcomeCall;
  programType: ProgramType;
  value: {
    provider: WelcomeCallProvider;
    /** Master switch for the program; absent on docs that predate it, which counts as enabled. */
    enabled?: boolean;
    /** Risk-decision signals; provider-independent. */
    riskDecision?: WelcomeCallRiskDecision;
    providerSettings: WelcomeCallProviderSettings;
  };
}

export enum WelcomeCallStipAttribute {
  age = 'age',
  state = 'state',
  salesRepStatus = 'salesRepStatus',
  quoteMonthlyPayment = 'quoteMonthlyPayment',
  preferredLanguage = 'preferredLanguage',
}

/** One admin-configurable criterion. Operator is implied by the attribute
 *  (>= for numeric; matches-any-of for multi-select) and is NOT stored. */
export interface WelcomeCallStipCriterion<TValue> {
  enabled: boolean;
  value?: TValue;
  /** Whole number 0-100: probability a matched criterion applies the stip. */
  applicationRate: number;
  applicationRates?: Record<string, number>;
  /** Per-item Recheck template id overrides (state criterion only); items without one use the program template. */
  templateIds?: Record<string, string>;
}

export interface WelcomeCallStipRulesValue {
  age: WelcomeCallStipCriterion<number>;
  state: WelcomeCallStipCriterion<string[]>;
  salesRepStatus: WelcomeCallStipCriterion<SalesRepStatus[]>;
  quoteMonthlyPayment: WelcomeCallStipCriterion<number>;
  preferredLanguage: WelcomeCallStipCriterion<Locale[]>;
}

/** Server-side response shape for the welcome-call stip rules endpoints.
 *  Use `Clientize<WelcomeCallStipRules>` for the client (RTK slice) type. */
export interface WelcomeCallStipRules {
  id: ObjectId;
  value: WelcomeCallStipRulesValue;
}

/** A program's full Welcome Call stip configuration as the admin endpoints expose it. */
export interface WelcomeCallProgramConfig extends WelcomeCallStipRules {
  programType: ProgramType;
  /** The program's Recheck template id; empty until an admin configures it. */
  templateId: string;
  /** Whether Welcome Call is on for the program. */
  enabled: boolean;
  /** A settings doc exists for the program, i.e. rules were saved at least once. */
  isConfigured: boolean;
}

export interface OperatingHoursTimeSlot {
  /** HH:MM in 24-hour format, or null when closed */
  open: string | null;
  close: string | null;
}

export interface OperatingHoursSpecificDate extends OperatingHoursTimeSlot {
  /** YYYY-MM-DD — the specific date this applies to */
  date: string;
}

export interface OperatingHoursValue {
  /** IANA timezone name, e.g. 'America/New_York'. Applies to all slots. CSV abbreviations (ET, CT, PT) are converted on import. */
  timezone: string;
  /** Default hours for Monday–Friday (null open/close = closed on weekdays) */
  weekday: OperatingHoursTimeSlot;
  /** Default hours for Saturday–Sunday (null open/close = closed on weekends) */
  weekend: OperatingHoursTimeSlot;
  /**
   * Per-date specificDates that cover both holidays (open: null, close: null) and partial days (open/close set to different hours).
   */
  specificDates: OperatingHoursSpecificDate[];
}

export interface OperatingHoursSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.qcPriorityOperatingHours;
  programType: ProgramType;
  value: OperatingHoursValue;
}

/**
 * Annex II domestic content serial reference, keyed by OEM/list identifier (e.g. 'qcells').
 * Matching is exact serial only — no prefix/range support.
 */
export interface AnnexIISerialReferenceValue {
  [oem: string]: {
    serials: string[];
  };
}

/** Percent values are 0-100. `snowLossPercent` is null where the location has no snow component (e.g. PR). */
export interface SystemLossThreshold {
  snowLossPercent: number | null;
  minSystemLossPercent: number;
}

export interface SystemLossCountyOverride extends SystemLossThreshold {
  name: string;
}

export interface SystemLossThresholdEntry extends SystemLossThreshold {
  stateCode: string;
  countyOverrides?: SystemLossCountyOverride[];
}

export interface SystemLossThresholdsValue {
  entries: SystemLossThresholdEntry[];
}

export interface SystemLossThresholds {
  id: ObjectId;
  value: SystemLossThresholdsValue;
}

export interface SystemLossThresholdsSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.systemLossThresholds;
  value: SystemLossThresholdsValue;
}

export interface ICancellationCronJobSettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.cancellationCronJobSettings;
  value: {
    inactivityDays: number;
    cancellationNote: string;
  };
}

export interface InstallAttestmentCopyItem {
  title: string;
  body: string;
}

export interface InstallAttestmentCopyEntry {
  title: string;
  body?: string;
  permitOptions?: Record<PermitAttestmentSelection, InstallAttestmentCopyItem>;
}

export interface InstallAttestmentCopySettingsDocument extends IAppSettingsDocument {
  type: AppSettingsTypes.installAttestmentCopy;
  programType: ProgramType;
  value: {
    version: string;
    items: Record<InstallAttestmentType, InstallAttestmentCopyEntry>;
  };
}
